import React, { useState, useEffect, lazy, Suspense } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from '@/contexts/AuthContext'; // Assuming updateUser, profile, profileLoading exist
import { authAPI } from '@/api'; // Assuming authAPI has email change methods
import { toast } from 'sonner'; // Use sonner directly
import { Loader2, Info, User, MapPin, Hash, Key, Mail, AlertCircle } from 'lucide-react';
import LocationSelector from '@/components/LocationSelector'; // Import directly for now
import type { LocationValue, OptionType, UserProfile, PersonCategory } from '@/types';
import { CATEGORY_OPTIONS } from '@/types'; // Import the constant
import { ScreenHeader, FormField as DesignFormField, StatusBadge } from '@/components/redesign';

const ProfileDetailScreen: React.FC = () => {
  const { t } = useTranslation();

  // Function to create a Zod enum from PersonCategory values
  const PersonCategoryEnum = z.enum(
      CATEGORY_OPTIONS.map(option => option.value) as [PersonCategory, ...PersonCategory[]]
  );

  // Schema for profile data (excluding phone and company for now based on requirements)
  const profileSchema = z.object({
    name: z.string().min(1, { message: t('profile.validation.name_required') }),
    email: z.string().email({ message: t('profile.validation.email_invalid') }),
    location: z.object({
      country: z.object({ value: z.string(), label: z.string() }).nullable().refine(val => val !== null, { message: t('profile.validation.country_required') }),
      city: z.object({ value: z.string(), label: z.string() }).nullable().refine(val => val !== null, { message: t('profile.validation.city_required') }),
    }).refine(val => val.country !== null && val.city !== null, { message: t('profile.validation.location_required') }),
    selected_role: PersonCategoryEnum.nullable().refine(val => val !== null, { message: t('profile.validation.role_required') }),
  });

  type ProfileFormData = z.infer<typeof profileSchema>;

  // Assuming these are provided by AuthContext, despite linter errors
  const { user, loading: userLoading, updateUser, profile, profileLoading, refreshUser } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [originalEmail, setOriginalEmail] = useState<string | null | undefined>(null);
  const [originalLocation, setOriginalLocation] = useState<LocationValue | null>(null);
  const [originalSelectedRole, setOriginalSelectedRole] = useState<PersonCategory | null | undefined>(null);

  // --- Email Change OTP State ---
  const [isEnteringOtp, setIsEnteringOtp] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [otpError, setOtpError] = useState("");
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [newEmail, setNewEmail] = useState(""); // Store the new email during verification
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  // --- End Email Change OTP State ---

  const form = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      name: '',
      email: '',
      location: { country: null, city: null },
      selected_role: null, // Default to null
    },
    mode: 'onChange',
  });

  const watchedEmail = form.watch("email");
  const emailIsDirty = watchedEmail !== originalEmail && originalEmail !== null;

  useEffect(() => {
    if (profile) {
      // Attempt to find matching OptionType for country/city based on labels from profile
      const initialLocation: LocationValue = {
        country: profile.country ? { value: profile.country, label: profile.country } : null,
        city: profile.city ? { value: profile.city, label: profile.city } : null,
      };

      const initialRole = PersonCategoryEnum.safeParse(profile.selected_role).success
          ? profile.selected_role as PersonCategory
          : null;

      form.reset({
        name: profile.name ?? '',
        email: profile.email ?? '',
        location: initialLocation,
        selected_role: initialRole,
      });
      setOriginalEmail(profile.email);
      setOriginalLocation(initialLocation);
      setOriginalSelectedRole(initialRole);
    } else if (user && !profileLoading) {
        form.reset({ email: user.email ?? ''});
        setOriginalEmail(user.email);
    }
  }, [profile, user, profileLoading, form]);

  // --- Email Change Functions ---

  const startEmailChange = async (email: string) => {
    setOtpError("");
    setIsSendingOtp(true);
    setIsLoading(true);
    setNewEmail(email); // Store the target email

    try {
        // TODO: Replace with actual API call - Assuming authAPI.sendEmailChangeOtp(email)
        await new Promise(resolve => setTimeout(resolve, 1000));
        const error = null; // Assume success for now

        if (error) {
            toast.error(t('profile.email_verification.code_send_failed'), { description: error.message || t('notifications.please_try_again') });
            setOtpError(error.message || t('profile.email_verification.code_send_failed'));
        } else {
            toast.info(t('profile.email_verification.code_sent', { email }));
            setIsEnteringOtp(true); // Show OTP form
            setOtpCode(""); // Clear previous OTP
        }
    } catch (err: any) {
        toast.error(t('profile.email_verification.code_send_error'));
        setOtpError(t('profile.email_verification.unknown_error'));
    } finally {
        setIsSendingOtp(false);
        setIsLoading(false);
    }
 };

 const handleOtpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setOtpError("");

    if (!/^\d{6}$/.test(otpCode)) {
        setOtpError(t('profile.email_verification.invalid_code'));
        return;
    }

    if (!newEmail) {
        setOtpError(t('profile.email_verification.no_email'));
        return;
    }

    setIsVerifyingOtp(true);
    setIsLoading(true);

    try {
        // TODO: Replace with actual API call
        await new Promise(resolve => setTimeout(resolve, 1000));
        const error = null; // Assume success for now
        const success = true;

        if (error || !success) {
            toast.error(t('profile.email_verification.verification_failed'), { description: error?.message || t('profile.email_verification.invalid_expired_code') });
            setOtpError(error?.message || t('profile.email_verification.invalid_expired_code'));
        } else {
            toast.success(t('profile.email_verification.verification_success'));
            setOriginalEmail(newEmail); // Update original email to the new verified one
            setIsEnteringOtp(false); // Hide OTP form
            setNewEmail("");
            setOtpCode("");
            // Optionally refresh user data from context if needed
            if (refreshUser) await refreshUser();
        }
    } catch (err: any) {
        toast.error(t('profile.email_verification.verification_error'));
        setOtpError(t('profile.email_verification.unknown_error'));
    } finally {
        setIsVerifyingOtp(false);
        setIsLoading(false);
    }
  };

  const cancelOtpFlow = () => {
    setIsEnteringOtp(false);
    setOtpError("");
    setOtpCode("");
    setNewEmail(""); // Clear the target email
    setIsLoading(false);
    setIsSendingOtp(false);
    setIsVerifyingOtp(false);
    // Reset email field in form back to original?
    form.setValue('email', originalEmail || '');
  };

  // --- End Email Change Functions ---

  const onSubmit = async (data: ProfileFormData) => {
    if (!user || !profile || !updateUser) {
        toast.error(t('profile.messages.loading_failed'));
        return;
    }

    setIsLoading(true);

    const emailChanged = data.email !== originalEmail;
    const updatePayload: Partial<UserProfile> = {};
    let needsProfileUpdate = false;

    // Prepare payload for profile table update (excluding email)
    if (data.name !== profile.name) {
        updatePayload.name = data.name;
        needsProfileUpdate = true;
    }
    if (JSON.stringify(data.location) !== JSON.stringify(originalLocation)) {
        updatePayload.country = data.location?.country?.label || null;
        updatePayload.city = data.location?.city?.label || null;
        needsProfileUpdate = true;
    }
     if (data.selected_role !== originalSelectedRole) {
        const validatedRole = PersonCategoryEnum.safeParse(data.selected_role);
        updatePayload.selected_role = validatedRole.success ? validatedRole.data : null;
        needsProfileUpdate = true;
     }

    try {
        // 1. Handle Profile Updates (non-email fields)
        if (needsProfileUpdate) {
            await updateUser(updatePayload); // updateUser should handle success/error toast internally

            // Update original values locally after successful update
            if (updatePayload.country !== undefined || updatePayload.city !== undefined) {
                 const newLocation: LocationValue = {
                        country: (data.location?.country?.value && data.location?.country?.label)
                                   ? { value: data.location.country.value, label: data.location.country.label }
                                   : null,
                        city: (data.location?.city?.value && data.location?.city?.label)
                                ? { value: data.location.city.value, label: data.location.city.label }
                                : null,
                    };
                setOriginalLocation(newLocation);
            }
            if (updatePayload.selected_role !== undefined) {
                const validatedRole = PersonCategoryEnum.safeParse(updatePayload.selected_role);
                setOriginalSelectedRole(validatedRole.success ? validatedRole.data : null);
            }
            // Name updates are reflected via profile context update
            toast.success(t('profile.messages.profile_updated')); // Add a specific success message here
        }

        // 2. Handle Email Change - Initiate OTP Flow
        if (emailChanged && data.email) {
             if (!needsProfileUpdate) {
                 toast.info(t('profile.email_verification.email_change_notice'));
             }
            await startEmailChange(data.email); // This will handle sending OTP and showing the form
        } else if (!needsProfileUpdate && !emailChanged) {
             toast.info(t('profile.messages.no_changes'));
        }

    } catch (error: any) {
      toast.error(t('profile.messages.profile_update_error'), {
        description: error.message || t('notifications.please_try_again'),
      });
    } finally {
      // Only set loading false if NOT entering OTP mode
      if (!isEnteringOtp) {
          setIsLoading(false);
      }
    }
  };

  if (userLoading || profileLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-[var(--gray-25,#FDFDFD)]">
        <Loader2 className="h-8 w-8 animate-spin text-brand-600" />
      </div>
    );
  }

  if (!user) {
      return (
        <div className="flex h-screen items-center justify-center bg-[var(--gray-25,#FDFDFD)]">
          <p className="text-error-600">{t('profile.messages.user_data_failed')}</p>
        </div>
      );
  }

   // If user exists but profile is still loading or null after loading, show appropriate message
   if (!profile && !profileLoading) {
       return (
         <div className="flex h-screen flex-col bg-[var(--gray-25,#FDFDFD)]">
            <ScreenHeader title={t('profile.title')} showBack />
            <main className="flex-grow p-6 flex items-center justify-center">
                <div className="text-center">
                    <p className="text-gray-500 mb-4">{t('profile.messages.profile_not_found')}</p>
                    <p className="text-sm text-gray-400 mb-2">
                      {t('profile.user_id')}: <span className="font-mono text-gray-600">{user.id}</span>
                    </p>
                    <p className="text-sm text-gray-400">
                      {t('profile.email')}: <span className="font-mono text-gray-600">{user.email}</span>
                    </p>
                 </div>
            </main>
         </div>
       );
   }

   if (isEnteringOtp) {
        return (
         <div className="flex flex-col min-h-screen bg-[var(--gray-25,#FDFDFD)]">
            <ScreenHeader title={t('profile.email_verification.title')} showBack={false} />
            <main className="flex-grow p-6 flex items-center justify-center">
                 <div className="w-full max-w-md space-y-6 px-6 py-8 bg-white border border-gray-200 rounded-2xl shadow-sm">
                     <h2 className="text-[18px] font-bold text-gray-900 text-center">{t('profile.email_verification.prompt')}</h2>
                     <p className="text-[13px] text-gray-500 text-center">
                         {t('profile.email_verification.instruction', { email: newEmail })}
                     </p>
                     <form onSubmit={handleOtpSubmit} className="space-y-4">
                         <DesignFormField
                           label={t('profile.email_verification.code_label')}
                           htmlFor="otp-code"
                           error={otpError || undefined}
                         >
                           <div className="relative">
                               <Hash className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                               <Input
                                   id="otp-code"
                                   type="text"
                                   inputMode="numeric"
                                   maxLength={6}
                                   placeholder={t('profile.email_verification.code_placeholder')}
                                   className="pl-9 border-gray-300 focus:border-brand-600 rounded-lg tracking-widest font-mono"
                                   value={otpCode}
                                   onChange={(e) => setOtpCode(e.target.value.replace(/[^0-9]/g, ''))}
                                   required
                               />
                           </div>
                         </DesignFormField>

                         <Button
                             type="submit"
                             className="w-full bg-brand-600 hover:bg-brand-700 text-white rounded-xl h-11 font-semibold"
                             disabled={isVerifyingOtp || isLoading}
                         >
                             {isVerifyingOtp ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                             {t('profile.email_verification.submit_button')}
                         </Button>
                         <Button
                             type="button"
                             variant="outline"
                             className="w-full mt-2 border-gray-300 text-gray-600 rounded-xl h-11"
                             onClick={cancelOtpFlow}
                             disabled={isVerifyingOtp || isLoading}
                         >
                             {t('profile.email_verification.cancel_button')}
                         </Button>
                     </form>
                 </div>
            </main>
         </div>
        );
   }

  return (
    <div className="flex flex-col min-h-screen bg-[var(--gray-25,#FDFDFD)]">
      <ScreenHeader title={t('profile.title')} showBack />

      <main className="flex-grow px-4 py-6 space-y-5 overflow-y-auto">
        {/* User ID (Read Only) */}
        <DesignFormField label={t('profile.user_id')} htmlFor="user-id">
          <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5">
            <Key className="h-4 w-4 text-gray-400 flex-shrink-0" />
            <span className="font-mono text-[13px] text-gray-500 truncate">{user.id}</span>
          </div>
        </DesignFormField>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
            {/* Name */}
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <DesignFormField
                    label={
                      <span className="flex items-center gap-1.5">
                        <User className="h-3.5 w-3.5" /> {t('profile.name')}
                      </span>
                    }
                    required
                    htmlFor="profile-name"
                    error={form.formState.errors.name?.message}
                  >
                    <Input
                      id="profile-name"
                      placeholder={t('profile.name_placeholder')}
                      className="border-gray-300 focus:border-brand-600 rounded-lg"
                      {...field}
                    />
                  </DesignFormField>
                </FormItem>
              )}
            />

            {/* Email */}
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <DesignFormField
                    label={
                      <span className="flex items-center gap-1.5">
                        <Mail className="h-3.5 w-3.5" /> {t('profile.email')}
                      </span>
                    }
                    required
                    htmlFor="profile-email"
                    error={form.formState.errors.email?.message}
                    hint={
                      emailIsDirty && !isEnteringOtp
                        ? <span className="flex items-center gap-1 text-warning-600">
                            <Info size={12} /> {t('profile.messages.email_change_required')}
                          </span>
                        : undefined
                    }
                  >
                    <Input
                      id="profile-email"
                      type="email"
                      placeholder={t('profile.email_placeholder')}
                      className="border-gray-300 focus:border-brand-600 rounded-lg"
                      {...field}
                    />
                  </DesignFormField>
                </FormItem>
              )}
            />

            {/* Selected Role */}
            <FormField
              control={form.control}
              name="selected_role"
              render={({ field }) => (
                <FormItem>
                  <DesignFormField
                    label={
                      <span className="flex items-center gap-1.5">
                        <User className="h-3.5 w-3.5" /> {t('profile.selected_role')}
                      </span>
                    }
                    required
                    htmlFor="profile-role"
                    error={form.formState.errors.selected_role?.message}
                  >
                    <Select
                      onValueChange={field.onChange}
                      value={field.value ?? ''}
                    >
                      <FormControl>
                        <SelectTrigger id="profile-role" className="border-gray-300 focus:border-brand-600 rounded-lg">
                          <SelectValue placeholder={t('profile.select_role')} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {CATEGORY_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </DesignFormField>
                </FormItem>
              )}
            />

            {/* Location */}
            <FormField
              control={form.control}
              name="location"
              render={({ field }) => {
                // Construct locationValue safely, ensuring OptionType structure or null
                const locationValue: LocationValue = {
                   country: (field.value?.country?.value && field.value?.country?.label)
                            ? { value: field.value.country.value, label: field.value.country.label }
                            : null,
                   city: (field.value?.city?.value && field.value?.city?.label)
                         ? { value: field.value.city.value, label: field.value.city.label }
                         : null,
                };

                return (
                  <FormItem>
                    <DesignFormField
                      label={
                        <span className="flex items-center gap-1.5">
                          <MapPin className="h-3.5 w-3.5" /> {t('profile.location')}
                        </span>
                      }
                      required
                      error={form.formState.errors.location?.message}
                    >
                      <LocationSelector
                          value={locationValue}
                          onChange={(newLocation) => {
                              field.onChange({
                                  country: newLocation.country,
                                  city: newLocation.city,
                              });
                          }}
                        />
                    </DesignFormField>
                  </FormItem>
                );
              }}
            />

            <Button
              type="submit"
              disabled={isLoading || userLoading || profileLoading || isSendingOtp || isVerifyingOtp}
              className="w-full bg-brand-600 hover:bg-brand-700 text-white rounded-xl h-11 font-semibold md:w-auto"
            >
              {(isLoading || isSendingOtp) ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {t('common.save')}
            </Button>
          </form>
        </Form>
      </main>
    </div>
  );
};

export default ProfileDetailScreen;
